import React, { useState, useEffect } from 'react'
import MetadataWizard, { type BatchWitnessMetadata } from './MetadataWizard'
import HomeChapterDashboard from './HomeChapterDashboard'
import HomeBookDashboard from './HomeBookDashboard'
import HomeAllBooksDashboard from './HomeAllBooksDashboard'
import { analyzeFiles, type FileAnalysis } from '../../lib/smartDropHandler'
import { useBooksStore } from '../../store/booksStore'
import { API_ENDPOINTS } from '../../config/api'
import { extractTextContent } from '../../lib/documentExtractor'
import { exportImportManager } from '../../lib/exportImportManager'
import type { ImportOptions } from '../../types/exportTypes'
import './HomePage.css'

interface HomePageProps { }

const HomePage: React.FC<HomePageProps> = () => {
    const [wizardOpen, setWizardOpen] = useState(false)
    const [fileAnalyses, setFileAnalyses] = useState<FileAnalysis[]>([])
    const [isProcessing, setIsProcessing] = useState(false)
    const [processingStatus, setProcessingStatus] = useState('')

    // Dashboard navigation state
    const [dashboardView, setDashboardView] = useState<'all-books' | 'book' | 'chapter'>('chapter')
    const [activeBookId, setActiveBookId] = useState<string | null>(null)
    const [activeChapterId, setActiveChapterId] = useState<string | null>(null)
    const [showManifesto, setShowManifesto] = useState(false)

    // Dark mode state
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('darkMode')
        return saved === 'true'
    })

    const { books, createBook, createChapter, createWitness, initializeDB } = useBooksStore()

    // Apply dark mode class to body
    useEffect(() => {
        if (isDarkMode) {
            document.body.classList.add('dark-mode')
        } else {
            document.body.classList.remove('dark-mode')
        }
        localStorage.setItem('darkMode', isDarkMode.toString())
    }, [isDarkMode])

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode)
    }

    // Calculate real counts for menu badges
    const totalBooks = books.length
    const totalChapters = books.reduce((sum, book) => {
        const chapters = useBooksStore.getState().getBookChapters(book.id)
        return sum + chapters.length
    }, 0)
    const totalWitnesses = books.reduce((sum, book) => {
        const chapters = useBooksStore.getState().getBookChapters(book.id)
        return sum + chapters.reduce((chapterSum, chapter) => {
            const witnesses = useBooksStore.getState().getChapterWitnesses(chapter.id)
            return chapterSum + witnesses.length
        }, 0)
    }, 0)
    const totalAlignments = books.reduce((sum, book) => {
        const chapters = useBooksStore.getState().getBookChapters(book.id)
        return sum + chapters.reduce((chapterSum, chapter) => {
            const alignments = useBooksStore.getState().getChapterAlignments?.(chapter.id) || []
            return chapterSum + alignments.length
        }, 0)
    }, 0)

    // Determine if we have active context (data present)
    const hasActiveContext = (totalBooks > 0 || totalWitnesses > 0) && !showManifesto

    const handleDropZoneClick = () => {
        const input = document.getElementById('homepage-file-input') as HTMLInputElement
        input?.click()
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.currentTarget.classList.add('drag-over')
    }

    const handleDragLeave = (e: React.DragEvent) => {
        e.currentTarget.classList.remove('drag-over')
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.currentTarget.classList.remove('drag-over')
        const files = e.dataTransfer.files
        if (files.length > 0) {
            handleFiles(files)
        }
    }

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files && files.length > 0) {
            handleFiles(files)
        }
    }

    const handleFiles = async (files: FileList) => {
        const analyses = await analyzeFiles(files)

        if (analyses.length === 0) return

        // Filter for raw text files
        const rawTextFiles = analyses.filter((a) => a.type === 'raw-text')

        if (rawTextFiles.length > 0) {
            // Show wizard for batch metadata collection
            setFileAnalyses(rawTextFiles)
            setWizardOpen(true)
            return
        }

        // Handle JSON files
        const jsonFiles = analyses.filter((a) => a.type.endsWith('-json'))
        if (jsonFiles.length > 0) {
            await handleJsonImport(jsonFiles)
            return
        }

        console.warn('No recognized file types')
    }

    const handleJsonImport = async (jsonFiles: FileAnalysis[]) => {
        try {
            for (const jsonFile of jsonFiles) {
                console.log(`Importing ${jsonFile.file.name}...`)
                const content = await jsonFile.file.text()

                const options: ImportOptions = {
                    merge_strategy: 'merge',
                    conflict_resolution: 'auto_resolve', // Auto-resolve conflicts for smooth UX
                    preserve_ids: false,
                    validate_integrity: true,
                    import_references_only: false,
                }

                const result = await exportImportManager.importData(content, options)

                if (result.success) {
                    // Refresh UI to show imported data in adaptive dashboard
                    await initializeDB()
                    console.log(`Successfully imported ${result.imported_items.length} items from ${jsonFile.file.name}`)
                    // The HomePage will automatically show Active Context view
                } else {
                    console.error(`Import failed for ${jsonFile.file.name}:`, result.errors)
                    alert(`Failed to import ${jsonFile.file.name}: ${result.errors.map((e) => e.message).join(', ')}`)
                }
            }
        } catch (error) {
            console.error('JSON import error:', error)
            alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
    }

    const handleWizardSubmit = async (metadata: BatchWitnessMetadata) => {
        if (fileAnalyses.length === 0) return

        setIsProcessing(true)
        setProcessingStatus('Creating book and chapter...')

        try {
            // Create Book
            const bookId = await createBook(metadata.bookTitle)

            // Create Chapter
            const chapterId = await createChapter(
                bookId,
                metadata.chapterLatin,
                `${metadata.chapterLatin} | ${metadata.chapterArabic}`
            )

            setProcessingStatus(`Preparing ${metadata.witnesses.length} witnesses...`)

            // Create all witnesses in batch with preprocessing
            const witnessIds: string[] = []
            for (let i = 0; i < metadata.witnesses.length; i++) {
                const witnessData = metadata.witnesses[i]

                setProcessingStatus(`Preprocessing ${witnessData.siglum} (${i + 1}/${metadata.witnesses.length})...`)

                // Extract text content (supports .txt, .docx, .rtf)
                const content = await extractTextContent(witnessData.file)

                // CRITICAL: Preprocess the text to extract chunks with trigger words
                console.log(`Preprocessing ${witnessData.siglum}...`)
                const preprocessingResult = await autoPreprocessText(content, witnessData.file.name)

                if (!preprocessingResult) {
                    console.error(`Failed to preprocess ${witnessData.siglum}, skipping...`)
                    continue
                }

                // Create TextFile object
                const uploadedFile = {
                    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    name: witnessData.file.name,
                    content,
                    size: content.length,
                    uploadedAt: Date.now(),
                }

                // Create Witness with preprocessing result
                const witnessId = await createWitness(chapterId, {
                    sigla: witnessData.siglum,
                    description: witnessData.description || witnessData.siglum,
                    library: witnessData.library || '',
                    uploaded_file: uploadedFile,
                    preprocessing_result: preprocessingResult,
                    last_modified: Date.now(),
                    version: 1,
                })

                witnessIds.push(witnessId)
                console.log(`Created witness ${witnessData.siglum} with ${preprocessingResult.primary_chunking?.total_primary_chunks || 0} chunks`)
            }

            // Close wizard and loading - Home will automatically show Active Context
            setWizardOpen(false)
            setFileAnalyses([])
            setIsProcessing(false)
            setProcessingStatus('')
            setShowManifesto(false)  // Return to dashboard after creating content
        } catch (error) {
            console.error('Failed to create witnesses:', error)
            setIsProcessing(false)
            setProcessingStatus('')
        }
    }

    // Auto-preprocessing helper (same as BatchWitnessUpload)
    const autoPreprocessText = async (text: string, filename: string) => {
        try {
            const response = await fetch(API_ENDPOINTS.preprocessingAuto, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text,
                    filename,
                }),
            })

            if (!response.ok) {
                throw new Error(`Preprocessing failed: ${response.status}`)
            }

            const result = await response.json()
            return result
        } catch (error) {
            console.error('Preprocessing error:', error)
            return null
        }
    }

    return (
        <div className="homepage">
            <header className="homepage-header">
                <div
                    className="homepage-logo"
                    onClick={() => {
                        setShowManifesto(true)
                        setActiveBookId(null)
                        setActiveChapterId(null)
                        setDashboardView('chapter')
                    }}
                    title="Return to home"
                >
                    <span className="logo-seg">seg</span>
                    <span className="logo-slash">/</span>
                    <span className="logo-ment">ment</span>
                    <span className="logo-arrow">›</span>
                    <span className="logo-align">align</span>
                    <span className="logo-assistant">assistant</span>
                </div>
                <div className="homepage-settings-btn" onClick={toggleDarkMode} title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}>
                    {isDarkMode ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="5" />
                            <line x1="12" y1="1" x2="12" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="23" />
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                            <line x1="1" y1="12" x2="3" y2="12" />
                            <line x1="21" y1="12" x2="23" y2="12" />
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                    )}
                </div>
            </header>

            <main className="homepage-main">
                {/* VIEW 1: CLEAN SLATE */}
                <div className={`view-clean-slate ${hasActiveContext ? 'hidden' : ''}`}>
                    <div className="homepage-content-left">
                        <div className="homepage-manifesto">
                            The <span className="logo-seg" style={{ fontSize: '0.9em' }}>seg</span>
                            <span className="logo-slash">/</span>
                            <span className="logo-ment" style={{ fontSize: '0.9em' }}>ment</span>
                            <span className="logo-arrow">›</span>
                            <span className="logo-align" style={{ fontSize: '0.9em' }}>align</span> assistant helps scholars <br />
                            <span className="keyword kw-seg">segment</span> manuscript texts, <br />
                            <span className="keyword kw-edit">edit</span> and refine boundaries, <br />
                            <span className="keyword kw-col">collate</span> multiple witnesses, <br />
                            <span className="keyword kw-align">align</span> them into a unified apparatus, <br />
                            and <span className="keyword kw-commit">commit</span> a synoptic edition.
                        </div>
                        {(totalBooks > 0 || totalWitnesses > 0) && (
                            <div
                                className="manifesto-dashboard-link"
                                onClick={() => setShowManifesto(false)}
                            >
                                ← View Dashboard
                            </div>
                        )}
                    </div>

                    <div className="homepage-content-right">
                        <div
                            className="homepage-drop-token"
                            onClick={handleDropZoneClick}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <svg className="drop-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            <span className="token-label">Drop Files</span>
                        </div>
                    </div>
                </div>

                {/* VIEW 2: ACTIVE CONTEXT */}
                <div className={`view-active-context ${hasActiveContext ? 'visible' : ''}`}>
                    {hasActiveContext && dashboardView === 'all-books' && (
                        <HomeAllBooksDashboard
                            onBookClick={(bookId) => {
                                setActiveBookId(bookId)
                                setDashboardView('book')
                            }}
                        />
                    )}
                    {hasActiveContext && dashboardView === 'book' && activeBookId && (
                        <HomeBookDashboard
                            bookId={activeBookId}
                            onChapterClick={(chapterId) => {
                                setActiveChapterId(chapterId)
                                setDashboardView('chapter')
                            }}
                            onAllBooksClick={() => {
                                setDashboardView('all-books')
                            }}
                        />
                    )}
                    {hasActiveContext && dashboardView === 'chapter' && (
                        <HomeChapterDashboard
                            chapterId={activeChapterId || undefined}
                            onAddWitness={handleDropZoneClick}
                            onBookClick={() => {
                                setDashboardView('all-books')
                            }}
                        />
                    )}
                </div>

                <input
                    type="file"
                    id="homepage-file-input"
                    className="homepage-hidden-input"
                    multiple
                    onChange={handleFileInput}
                />
            </main>

            <footer className="homepage-footer">
                <nav className="homepage-menu">
                    <div
                        className={`homepage-menu-item ${dashboardView === 'all-books' && !showManifesto ? 'active' : ''}`}
                        onClick={() => {
                            setShowManifesto(false)
                            setDashboardView('all-books')
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        Books <span className="homepage-menu-badge">{totalBooks}</span>
                    </div>
                    <div
                        className={`homepage-menu-item ${dashboardView === 'book' && !showManifesto ? 'active' : ''}`}
                        onClick={() => {
                            if (totalChapters > 0) {
                                setShowManifesto(false)
                                setDashboardView('book')
                                // If no book is selected, select the first one
                                if (!activeBookId && books.length > 0) {
                                    setActiveBookId(books[0].id)
                                }
                            }
                        }}
                        style={{ cursor: totalChapters > 0 ? 'pointer' : 'default', opacity: totalChapters > 0 ? 1 : 0.5 }}
                    >
                        Chapters <span className="homepage-menu-badge">{totalChapters}</span>
                    </div>
                    <div
                        className={`homepage-menu-item ${dashboardView === 'chapter' && !showManifesto ? 'active' : ''}`}
                        onClick={() => {
                            if (totalWitnesses > 0) {
                                setShowManifesto(false)
                                setDashboardView('chapter')
                            }
                        }}
                        style={{ cursor: totalWitnesses > 0 ? 'pointer' : 'default', opacity: totalWitnesses > 0 ? 1 : 0.5 }}
                    >
                        Witnesses <span className="homepage-menu-badge">{totalWitnesses}</span>
                    </div>
                    <div
                        className="homepage-menu-item"
                        onClick={() => totalAlignments > 0 && (window.location.hash = '#/books')}
                        style={{ cursor: totalAlignments > 0 ? 'pointer' : 'default', opacity: totalAlignments > 0 ? 1 : 0.5 }}
                    >
                        Alignments <span className="homepage-menu-badge">{totalAlignments}</span>
                    </div>
                </nav>

                <div className="homepage-bottom-bar">
                    <div className="homepage-imprint">
                        Conceived and designed by <strong>Walīd</strong> between Tetouan & Berlin, in collaboration with the
                        eager <strong>Claude</strong> models, the composed <strong>Gemini 2.5 Pro</strong>, and the precise{' '}
                        <strong>GPT-5</strong> and <strong>5.1</strong>. Occasional help was received from <strong>Kimi K2</strong>{' '}
                        and <strong>Qwen Code</strong>. All code was AI generated.
                    </div>
                    <div className="homepage-version">Beta 1.0</div>
                </div>
            </footer>

            {/* Loading Overlay */}
            {isProcessing && (
                <div className="homepage-loading-overlay">
                    <div className="homepage-loading-content">
                        <div className="homepage-loading-spinner" />
                        <div className="homepage-loading-text">{processingStatus}</div>
                    </div>
                </div>
            )}

            <MetadataWizard
                isOpen={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onSubmit={handleWizardSubmit}
                fileAnalyses={fileAnalyses}
            />
        </div>
    )
}

export default HomePage
